/*
  Mobile sticky buy bar for the luxury product page.

  ── Why this exists ─────────────────────────────────────────────────────────
  The PDP is long: after the buy box come the trust row, three assurance
  accordions, the craft link, and then five more full-width sections plus the
  Judge.me review widget. On a phone the inline Add to cart scrolls out of view
  early and never comes back, so the shoppers who read the most — the ones
  furthest along — are the ones who have to scroll furthest to act.

  ── Why it proxies instead of rendering its own form ────────────────────────
  The button below dispatches a click on the real <product-form> submit button
  rather than posting to /cart/add itself. That keeps one code path for the
  spinner, the error message, the skip-cart setting and the cart drawer: if any
  of those change, this bar follows automatically. A second form would have to
  reimplement all of it and would drift.

  ── Visibility rule ─────────────────────────────────────────────────────────
  Shown only once the inline button has scrolled ABOVE the viewport — never
  while it is still on screen, and deliberately NOT while it is still below the
  fold. Hidden again once the footer comes into view, so the bar never sits on
  top of the footer's links and policy pages.

  ── Do not "fix" the gap at the top of the page ─────────────────────────────
  This rule leaves a stretch at the very top with no call to action visible at
  all. That is known, measured, and intended. On a 390x700 viewport — a real
  phone once the browser chrome is counted — the landing page reads:

      scrollY    0    button below fold    bar hidden    no CTA on screen
      scrollY   50    button below fold    bar hidden    no CTA on screen
      scrollY  150    button below fold    bar hidden    no CTA on screen
      scrollY  400    button in view       bar hidden    CTA present

  Dropping the direction check from the observer below closes that gap, and it
  was tried. It was reverted on purpose: the bar carries the price, and showing
  it at scroll 0 makes the price the first thing a visitor sees, ahead of the
  watch. For this brand the photograph has to land first — the price is meant to
  arrive after the object has done its work, which is the same reasoning the buy
  box itself is built on. Trading a little first-screen CTA availability for
  that is a positioning decision, not an oversight.

  If this ever needs revisiting, the move is a bar that shows early WITHOUT the
  price, not a bar that shows the price early.
*/
(function () {
  if (!('IntersectionObserver' in window)) return;

  function init(bar) {
    var target = document.getElementById(bar.dataset.stickyBuyTarget);
    if (!target) return;

    var button = bar.querySelector('.luxury-sticky-buy__button');
    if (!button) return;

    var scrolledPast = false;
    var footerVisible = false;

    function sync() {
      var show = scrolledPast && !footerVisible;
      bar.classList.toggle('is-visible', show);
      /*
        Two app embeds park a fixed launcher in the bottom-left corner and both
        outrank this bar's stacking context. Lifting them by the bar's height
        keeps them tappable instead of buried under it. Uses the `translate`
        property rather than `transform` so it cannot clobber a transform the
        apps set on their own elements.
      */
      document.documentElement.classList.toggle('luxury-sticky-buy-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    button.addEventListener('click', function () {
      target.click();
    });

    /*
      product-form.js marks the real button aria-disabled while the add is in
      flight. Mirroring it stops a double tap here queueing a second add.
    */
    new MutationObserver(function () {
      button.disabled = target.disabled || target.getAttribute('aria-disabled') === 'true';
    }).observe(target, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });

    new IntersectionObserver(
      function (entries) {
        var entry = entries[entries.length - 1];
        /* `top < 0` is the whole rule: out of view ABOVE the viewport counts,
           out of view BELOW it does not. See the note in the header. */
        scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sync();
      },
      { threshold: 0 }
    ).observe(target);

    var footer = document.querySelector('.shopify-section-group-footer-group');
    if (footer) {
      new IntersectionObserver(
        function (entries) {
          footerVisible = entries[entries.length - 1].isIntersecting;
          sync();
        },
        { threshold: 0 }
      ).observe(footer);
    }

    sync();
  }

  document.querySelectorAll('.luxury-sticky-buy').forEach(init);
})();
